import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { LoadingState } from '@/components/ui/loading-state';
import { AlertTriangle, Shield, CreditCard } from 'lucide-react';
import { usePermissions } from '@/contexts/unified-permissions';
import { useSubscription } from '@/hooks/subscription';
import { ReauthenticationService } from '@/services/auth/ReauthenticationService';
import { AccountDeletionService } from '@/services/account/AccountDeletionService';
import { isIOSNativeApp } from '@/utils/platformUtils';
import { toast } from 'sonner';

interface AccountDeletionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type DeletionStep = 'warning' | 'subscription-check' | 'reauthentication' | 'confirmation' | 'processing';

export function AccountDeletionModal({ open, onOpenChange }: AccountDeletionModalProps) {
  const { user, session } = usePermissions();
  const isIOSNative = isIOSNativeApp();
  const { isSubscribed, isLoading: subscriptionLoading } = useSubscription();
  
  const [currentStep, setCurrentStep] = useState<DeletionStep>('warning');
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setCurrentStep('warning');
      setPassword('');
      setConfirmationText('');
      setHasActiveSubscription(false);
      setIsProcessing(false);
      setError('');
      setAgreedToTerms(false);
    }
  }, [open]);

  // Check subscription status when modal opens
  useEffect(() => {
    const checkSubscription = async () => {
      if (open && !subscriptionLoading) {
        // Use both the hook and the service for comprehensive checking
        const serviceResult = await AccountDeletionService.checkSubscriptionStatus();
        const hasSubscription = isSubscribed || serviceResult.hasActiveSubscription;
        setHasActiveSubscription(hasSubscription);
      }
    };

    checkSubscription();
  }, [open, isSubscribed, subscriptionLoading]);

  const handleClose = () => {
    if (!isProcessing) {
      onOpenChange(false);
    }
  };

  const handleNextStep = () => {
    setError('');
    
    switch (currentStep) {
      case 'warning':
        if (!agreedToTerms) {
          setError('You must acknowledge the warning to continue.');
          return;
        }
        setCurrentStep('subscription-check');
        break;
      case 'subscription-check':
        if (hasActiveSubscription) {
          setError('Please cancel your subscription before deleting your account.');
          return;
        }
        setCurrentStep('reauthentication');
        break;
      case 'reauthentication':
        handleReauthentication();
        break;
      case 'confirmation':
        handleAccountDeletion();
        break;
    }
  };

  const handleReauthentication = async () => {
    if (!user || !session) {
      setError('Session expired. Please sign in again.');
      return;
    }

    // Check if OAuth user needs different handling
    if (ReauthenticationService.getOAuthProvider(user)) {
      const provider = ReauthenticationService.getOAuthProvider(user);
      setError(`Please use ${provider} to reauthenticate for account deletion.`);
      return;
    }

    if (!password.trim()) {
      setError('Please enter your password to continue.');
      return;
    }

    setIsProcessing(true);
    try {
      const result = await ReauthenticationService.reauthenticate({
        user,
        session,
        password
      });

      if (result.success) {
        setCurrentStep('confirmation');
      } else {
        setError(result.error || 'Reauthentication failed. Please try again.');
      }
    } catch (error) {
      setError('Reauthentication failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAccountDeletion = async () => {
    if (confirmationText !== 'DELETE') {
      setError('Please type "DELETE" to confirm account deletion.');
      return;
    }

    setIsProcessing(true);
    setCurrentStep('processing');

    try {
      const result = await AccountDeletionService.deleteAccount({
        password,
        confirmationText
      });

      if (result.success) {
        toast.success(result.message);
        onOpenChange(false);

        // Sign out user after successful deletion
        // The user will be redirected to login page
        window.location.href = '/auth/login?message=account-deleted';
      } else {
        setCurrentStep('confirmation');
        setError(result.error || 'Failed to delete account. Please try again or contact support.');
      }
    } catch (error) {
      setCurrentStep('confirmation');
      setError('Failed to delete account. Please try again or contact support.');
    } finally {
      setIsProcessing(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'warning':
        return (
          <div className="space-y-4">
            <Alert className="border-destructive/50 bg-destructive/5">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <AlertDescription className="text-destructive">
                <strong>Warning:</strong> This action is permanent and cannot be undone.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-3 text-sm">
              <p>Deleting your account will:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Permanently remove your profile and personal information</li>
                <li>Delete all your training journal entries and notes</li>
                <li>Remove your saved techniques and game plans</li>
                <li>Cancel access to premium features</li>
                <li>Remove your account from our systems within 30 days</li>
              </ul>
              
              <p className="font-medium">Content you've shared with others may remain available to them.</p>
              
              <p>If you have an active subscription, please cancel it first through your subscription settings.</p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox 
                id="agree-terms" 
                checked={agreedToTerms}
                onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
              />
              <Label htmlFor="agree-terms" className="text-sm">
                I understand that this action is permanent and cannot be undone
              </Label>
            </div>
          </div>
        );

      case 'subscription-check':
        if (subscriptionLoading) {
          return <LoadingState title="Checking subscription status..." size="sm" />;
        }
        
        if (hasActiveSubscription) {
          return (
            <div className="space-y-4">
              <Alert className="border-destructive/50 bg-destructive/5">
                <CreditCard className="h-4 w-4 text-destructive" />
                <AlertDescription className="text-destructive">
                  <strong>Active Subscription Detected</strong>
                </AlertDescription>
              </Alert>
              
              <p className="text-sm">
                You currently have an active subscription. Please cancel your subscription before deleting your account.
              </p>
              
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  if (isIOSNative) {
                    // Deep link to App Store subscription management for iOS
                    window.open('https://apps.apple.com/account/subscriptions', '_system');
                  } else {
                    // Open Stripe billing portal for web/Android
                    window.open('https://billing.stripe.com/p/login/test_28o14rcIu9QdeDucYY', '_blank');
                  }
                }}
              >
                Manage Subscription
              </Button>
            </div>
          );
        }
        
        return (
          <div className="space-y-4">
            <div className="flex items-center space-x-2 text-green-600">
              <Shield className="h-4 w-4" />
              <span className="text-sm font-medium">No active subscription found</span>
            </div>
            <p className="text-sm text-muted-foreground">
              You can proceed with account deletion.
            </p>
          </div>
        );

      case 'reauthentication':
        return (
          <div className="space-y-4">
            <Alert>
              <Shield className="h-4 w-4" />
              <AlertDescription>
                For security, please enter your password to confirm your identity.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <Label htmlFor="password">Current Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your current password"
                disabled={isProcessing}
              />
            </div>
          </div>
        );

      case 'confirmation':
        return (
          <div className="space-y-4">
            <Alert className="border-destructive/50 bg-destructive/5">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <AlertDescription className="text-destructive">
                <strong>Final Confirmation Required</strong>
              </AlertDescription>
            </Alert>
            
            <p className="text-sm">
              This is your final chance to cancel. Once confirmed, your account and all data will be permanently deleted.
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="confirmation">Type "DELETE" to confirm</Label>
              <Input
                id="confirmation"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Type DELETE here"
                disabled={isProcessing}
              />
            </div>
          </div>
        );

      case 'processing':
        return (
          <div className="py-8">
            <LoadingState 
              title="Deleting Account..." 
              description="Please wait while we process your account deletion request."
              size="md"
            />
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'warning': return 'Delete Account';
      case 'subscription-check': return 'Subscription Check';
      case 'reauthentication': return 'Verify Identity';
      case 'confirmation': return 'Final Confirmation';
      case 'processing': return 'Processing...';
      default: return 'Delete Account';
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case 'warning': return 'Please read the following information carefully before proceeding.';
      case 'subscription-check': return 'Checking for active subscriptions...';
      case 'reauthentication': return 'We need to verify your identity before proceeding.';
      case 'confirmation': return 'This action cannot be undone. Please confirm your decision.';
      case 'processing': return 'Your account deletion request is being processed.';
      default: return '';
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'warning': return agreedToTerms;
      case 'subscription-check': return !hasActiveSubscription;
      case 'reauthentication': return password.trim().length > 0;
      case 'confirmation': return confirmationText === 'DELETE';
      default: return false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col" onPointerDownOutside={(e) => isProcessing && e.preventDefault()}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg font-semibold text-destructive">
            {getStepTitle()}
          </DialogTitle>
          <DialogDescription>
            {getStepDescription()}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 min-h-0">
          {renderStepContent()}

          {error && (
            <Alert className="border-destructive/50 bg-destructive/5">
              <AlertTriangle className="h-4 w-4 text-destructive" />
              <AlertDescription className="text-destructive">
                {error}
              </AlertDescription>
            </Alert>
          )}
        </div>

        {currentStep !== 'processing' && (
          <div className="flex justify-between pt-4 flex-shrink-0 border-t">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>

            {currentStep !== 'subscription-check' || !hasActiveSubscription ? (
              <Button
                variant="destructive"
                onClick={handleNextStep}
                disabled={!canProceed() || isProcessing}
              >
                {isProcessing ? 'Processing...' :
                 currentStep === 'confirmation' ? 'Delete Account' : 'Continue'}
              </Button>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
